/**
 * Home feed tidying:
 *  - Previews that run into a table (CPI reports: "Key facts | Measure | Aug 2026 | ...") show only the
 *    text before the table: beforeTable(), used by the plain-text clean-up in index.js (FoF Synopsis's and
 *    flarum/sticky's previews) and here for Synopsis's HTML-based ("rich") previews.
 *  - The pin badge shows on tag pages only, not on the home feed.
 *  - At most one discussion with a poll on the home feed (the most recent one; the others stay in their tags).
 *    Guests don't see polls with fewer than 5 voters there.
 */
import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import Discussion from 'flarum/common/models/Discussion';
import DiscussionListItem from 'flarum/forum/components/DiscussionListItem';
import { truncate } from 'flarum/common/utils/string';
import { highlightIds } from './TagAbout';
export function isHomeFeed() {
  try {
    return window.location.pathname === '/' && !m.route.param('tags');
  } catch (e) {
    return false;
  }
}

/** Text with a markdown table in it: the text before the table, ending at its last full sentence */
export function beforeTable(text) {
  const s = String(text || '');
  const pipes = (s.match(/\|/g) || []).length;
  if (pipes < 3 || !/\|\s*:?-{3,}/.test(s.replace(/\s+/g, ' ')) && pipes < 6) return null;
  let cut = s.slice(0, s.indexOf('|')).replace(/(\*\*|__)/g, '').replace(/(^|\s)#{1,6}\s+/g, '$1').replace(/\s+/g, ' ').trim();
  // drop a heading or label left just before the table ("Key facts")
  const sentence = cut.match(/^[\s\S]*[.!?](?=\s|[A-Z]|$)/);
  if (sentence && cut.length - sentence[0].length < 40) cut = sentence[0].trim();
  return cut;
}
function htmlText(html) {
  const doc = new DOMParser().parseFromString(String(html || ''), 'text/html');
  // block elements end with a space, so "August.</p><h2>Key facts" doesn't run together
  doc.body.querySelectorAll('p, h1, h2, h3, h4, h5, h6, li, tr, br, div').forEach(el => el.after(' '));
  return doc.body.textContent || '';
}

/** For guests, a poll with fewer than 5 voters is left off the home feed (it looks empty to a first-time visitor) */
const GUEST_MIN_VOTERS = 5;
function isQuietPoll(d) {
  if (app.session.user) return false;
  const n = d.attribute('vouchaPollVoters');
  return typeof n === 'number' && n < GUEST_MIN_VOTERS;
}

/** On the home feed, one poll: the most recent one (that isn't quiet, for guests); the others stay in their tags */
function isExtraPoll(d) {
  if (!d || !d.attribute || !d.attribute('hasPoll') || !isHomeFeed() || !app.discussions) return false;
  if (isQuietPoll(d)) return true;
  let first = null;
  try {
    app.discussions.getPages().some(page => page.items.some(x => x && x.attribute && x.attribute('hasPoll') && !isQuietPoll(x) ? (first = x, true) : false));
  } catch (e) {
    return false;
  }
  return !!first && first !== d;
}

// late (-100), so flarum/sticky has added its badge before it is taken off
app.initializers.add('voucha-theme-feed-tidy', function () {
  // previews that start with a table
  override('ext:fof/synopsis/forum/components/Excerpt', 'getContent', function (original) {
    let text = null;
    try {
      text = this.richExcerpt ? htmlText(this.contentRich()) : this.contentPlain();
    } catch (e) {
      return original();
    }
    const cut = beforeTable(text);
    return cut ? truncate(cut, this.length || 200) : original();
  });

  // the pin badge: tag pages only
  extend(Discussion.prototype, 'badges', function (items) {
    if (items.has('sticky') && isHomeFeed()) items.remove('sticky');
  });

  // one poll on the home feed
  extend(DiscussionListItem.prototype, 'elementAttrs', function (attrs) {
    if (isExtraPoll(this.attrs.discussion)) attrs.className = (attrs.className || '') + ' is-vouchaExtraPoll';
  });

  // Feed rows the reader doesn't see (extra polls on the home feed, a tag's highlighted posts), so other
  // extensions can count only visible discussions (voucha-ads places its cards "after the 3rd discussion")
  app.vouchaFeedHidden = function (d) {
    if (!d) return false;
    if (isExtraPoll(d)) return true;
    let slug = '';
    try {
      slug = m.route.param('tags') || '';
    } catch (e) {}
    return !!slug && highlightIds(slug).indexOf(String(d.id())) !== -1;
  };
}, -100);